// Firebase 설정 및 인증 관련 모듈 불러오기
import { auth, database, requestNotificationPermission, setupNotificationListener } from './firebase-config.js';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, set, onValue, get } from 'firebase/database';

// 상태 관리
import { store } from '../store/index.js';
import { setUser, clearUser } from '../store/actions/userActions.js';

// UI 컴포넌트 및 관리자
import { renderPosts } from '../components/post.js';
import { updateUserUI } from '../components/user.js';
import { showToast } from './ui-manager.js';
import { userCounter } from './user-counter.js';

/**
 * 앱 초기화 함수
 * - Firebase 인증 상태 리스너 설정
 * - 초기 UI 렌더링
 */
export const initApp = async () => {
  try {
    console.log('앱을 초기화하는 중...');
    
    // 서비스 워커 등록
    if ('serviceWorker' in navigator) {
      try {
        // 주 서비스 워커 등록 (PWA용)
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        console.log('PWA 서비스 워커가 성공적으로 등록되었습니다:', registration.scope);
        
        // Firebase 메시징용 서비스 워커 등록 (필요한 경우)
        const messagingRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/firebase-cloud-messaging-push-scope'
        });
        console.log('Firebase 메시징 서비스 워커가 성공적으로 등록되었습니다:', messagingRegistration.scope);
      } catch (error) {
        console.error('서비스 워커 등록 중 오류 발생:', error);
      }
    }
    
    // 뒤로가기 이벤트 처리 설정
    setupBackButtonHandler();
    
    // 사용자 카운터 시작
    userCounter.start();
    
    // 인증 상태 변경 리스너
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // 사용자가 로그인한 경우
        console.log('사용자 로그인 상태:', user.email);
        
        // 사용자 정보를 스토어에 저장
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '사용자',
          photoURL: user.photoURL || 'assets/default-avatar.png',
        };
        
        store.dispatch(setUser(userData));
        
        // UI 업데이트
        updateUserUI(userData);
        
        // [DEVICE_PROTECTION] 사용자 디바이스 경로 보호 기능
        await protectUserDevicesPath(user.uid);
        
        // [DEVICE_PROTECTION] 실시간 디바이스 경로 감시 시작
        setupDevicePathWatcher(user.uid);
        
        // FCM 알림 권한 요청 및 토큰 저장
        initNotifications(user.uid);
        
        // 게시물 불러오기
        await renderPosts();
        
        // 로그인 성공 메시지
        showToast('로그인되었습니다.');
      } else {
        // 사용자가 로그아웃한 경우
        console.log('로그인되지 않은 상태');
        
        // [DEVICE_PROTECTION] 디바이스 경로 감시 중지
        stopDevicePathWatcher();
        
        // 스토어의 사용자 정보 초기화
        store.dispatch(clearUser());
        
        // UI 업데이트 (로그인 버튼 표시 등)
        updateUserUI(null);
      }
    });
    
    // URL 파라미터 처리
    handleURLParameters();
    
    console.log('앱 초기화 완료');
    return true;
  } catch (error) {
    console.error('앱 초기화 중 오류 발생:', error);
    showToast('앱 초기화 중 오류가 발생했습니다.', 'error');
    return false;
  }
};

/**
 * FCM 알림 초기화
 * @param {string} userId - 사용자 UID
 */
const initNotifications = async (userId) => {
  try {
    // 알림 권한 요청 및 FCM 토큰 가져오기
    const token = await requestNotificationPermission();
    
    if (token) {
      // 토큰을 Firebase DB에 저장 (토큰 자체를 키로 사용하되 특수문자 대체)
      const sanitizedToken = token.replace(/[.#$/\[\]]/g, '_');
      const fcmTokenRef = ref(database, `users/${userId}/fcmTokens/${sanitizedToken}`);
      await set(fcmTokenRef, token);
      
      // 토큰 메타데이터 저장
      const fcmTokenMetaRef = ref(database, `users/${userId}/fcmTokensMeta`);
      await set(fcmTokenMetaRef, {
        lastUpdated: new Date().toISOString(),
        platform: 'web',
        browser: getBrowserInfo(),
        userAgent: navigator.userAgent
      });
      
      console.log('FCM 토큰이 데이터베이스에 저장되었습니다.');
      
      // 가입된 채팅방 목록 가져오기
      const userChatsRef = ref(database, `users/${userId}/chats`);
      onValue(userChatsRef, (snapshot) => {
        if (snapshot.exists()) {
          const chatRooms = snapshot.val();
          
          // 각 채팅방에 대한 알림 주제 구독 설정
          Object.keys(chatRooms).forEach((chatRoomId) => {
            console.log(`채팅방 ${chatRoomId}에 대한 알림 설정 완료`);
            // 실제 구현에서는 Firebase Cloud Messaging 주제 구독 설정
            // 현재 웹에서는 직접적인 주제 구독이 제한되어 있으므로 서버 측에서 처리해야 함
          });
        }
      }, { onlyOnce: true });
      
      // 알림 수신 리스너 설정
      setupNotificationListener((payload) => {
        console.log('알림 수신:', payload);
        
        // 알림 데이터 처리
        if (payload && payload.data) {
          const data = payload.data;
          
          // 알림 유형에 따른 처리
          if (data.type === 'chat_message' && data.chatRoomId) {
            showToast('새 메시지: ' + (payload.notification?.body || '새 채팅 메시지가 도착했습니다.'));
            
            // 채팅 패널이 이미 열려있는지 확인
            const chatPanel = document.querySelector(`[data-chat-room-id="${data.chatRoomId}"]`);
            if (!chatPanel) {
              // TODO: 채팅방 패널 열기 (패널 관리자 구현 후 추가)
              console.log(`채팅방(${data.chatRoomId}) 열기 필요`);
            }
          }
        }
      });
    }
  } catch (error) {
    console.error('알림 초기화 오류:', error);
  }
};

/**
 * 브라우저 정보 가져오기
 * @returns {string} 브라우저 이름과 버전
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  
  if (ua.indexOf("Firefox") > -1) {
    browser = "Firefox";
  } else if (ua.indexOf("SamsungBrowser") > -1) {
    browser = "Samsung Browser";
  } else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) {
    browser = "Opera";
  } else if (ua.indexOf("Edge") > -1) {
    browser = "Edge";
  } else if (ua.indexOf("Chrome") > -1) {
    browser = "Chrome";
  } else if (ua.indexOf("Safari") > -1) {
    browser = "Safari";
  }
  
  return browser;
};

/**
 * URL 파라미터 처리
 */
const handleURLParameters = () => {
  // URLSearchParams를 사용하여 URL 파라미터 가져오기
  const urlParams = new URLSearchParams(window.location.search);
  const chatRoomId = urlParams.get('chatRoom');
  
  if (chatRoomId) {
    // chatRoomId 파라미터가 있으면 채팅방 열기
    console.log(`URL 파라미터로 전달된 채팅방(${chatRoomId}) 열기`);
    
    // 로그인 상태 확인
    const user = auth.currentUser;
    if (user) {
      // 로그인된 경우 채팅방 열기
      openChatRoomFromParam(chatRoomId);
    } else {
      // 로그인되지 않은 경우 로그인 후 채팅방 열 수 있도록 저장
      localStorage.setItem('pendingChatRoom', chatRoomId);
      showToast('채팅방에 참여하려면 로그인이 필요합니다.', 'warning');
    }
  }
};

/**
 * URL 파라미터로 전달된 채팅방 열기
 * @param {string} chatRoomId - 채팅방 ID
 */
const openChatRoomFromParam = async (chatRoomId) => {
  try {
    // 채팅방 정보 가져오기
    const chatRoomRef = ref(database, `chatingRooms/${chatRoomId}`);
    onValue(chatRoomRef, (snapshot) => {
      if (snapshot.exists()) {
        // 채팅방이 존재하면 패널 열기
        const chatRoom = snapshot.val();
        
        // TODO: 채팅 패널 열기 함수 호출
        // 이 부분은 DraggablePanel을 이용한 채팅 UI 구현 완료 후 추가
        
        // URL 파라미터 제거
        const url = new URL(window.location);
        url.searchParams.delete('chatRoom');
        window.history.pushState({}, '', url);
      } else {
        showToast('존재하지 않는 채팅방입니다.', 'error');
      }
    }, { onlyOnce: true });
  } catch (error) {
    console.error('채팅방 정보 가져오기 오류:', error);
    showToast('채팅방 정보를 가져오는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 뒤로가기 버튼 처리 핸들러 설정
 */
const setupBackButtonHandler = () => {
  // 채팅창 상태 추적 변수
  window.isChatUIOpen = false;
  
  // 앱 내 네비게이션 플래그 초기화
  window.appNavigation = {
    internalNavigation: false,
    lastState: 'SNS3_main',
    shortcutClicked: false
  };
  
  // 더미 히스토리 상태 추가
  window.history.pushState({ page: 'main' }, '', window.location.href);
  
  // 뒤로가기 이벤트 처리
  window.addEventListener('popstate', function(event) {
    // 내부 네비게이션인 경우 처리
    if (window.appNavigation && (window.appNavigation.internalNavigation || window.appNavigation.shortcutClicked)) {
      // 내부 네비게이션 플래그 초기화
      window.appNavigation.internalNavigation = false;
      window.appNavigation.shortcutClicked = false;
      // 히스토리 상태 복원
      window.history.pushState({ page: 'main' }, '', window.location.href);
      return;
    }
    
    // 채팅창이 열려있는 경우 채팅창만 닫기
    if (window.isChatUIOpen) {
      event.preventDefault();
      const chatUIContainer = document.getElementById('chat-ui-container');
      const postsContainer = document.getElementById('posts-container');
      
      if (chatUIContainer && postsContainer) {
        chatUIContainer.style.display = 'none';
        postsContainer.style.display = 'block';
        
        // 채팅창 상태 업데이트
        window.isChatUIOpen = false;
        
        // 더미 히스토리 상태 다시 추가
        window.history.pushState({ page: 'main' }, '', window.location.href);
      }
    } else {
      // // 채팅창이 닫혀있는 경우 앱 종료 전 확인
      // const confirmExit = confirm('앱을 종료하시겠습니까?');
      
      // if (!confirmExit) {
      //   // 사용자가 종료를 취소한 경우 더미 히스토리 상태 다시 추가
      //   window.history.pushState({ page: 'main' }, '', window.location.href);
      // } else {
      //   // 사용자가 종료를 확인한 경우 앱 종료
      //   window.history.go(-1);
      // }
    }
  });
};

/**
 * 사용자 디바이스 경로 보호 기능
 * 로그인 시 해당 사용자의 devices 경로가 존재하는지 확인하고,
 * 없다면 빈 배열로 초기화하여 의도치 않은 삭제를 방지
 * @param {string} userId - 사용자 UID
 */
const protectUserDevicesPath = async (userId) => {
  try {
    if (!userId) {
      console.warn('[DEVICE_PROTECTION] 사용자 ID가 제공되지 않았습니다.');
      return;
    }

    console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 보호 검사 시작`);
    
    const devicesRef = ref(database, `users/${userId}/devices`);
    const snapshot = await get(devicesRef);
    
    if (!snapshot.exists()) {
      // 디바이스 경로가 없는 경우 빈 배열로 초기화
      console.warn(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로가 없습니다. 빈 배열로 초기화합니다.`);
      
      try {
        await set(devicesRef, []);
        console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로를 빈 배열로 복원했습니다.`);
        showToast('디바이스 목록이 안전하게 초기화되었습니다.', 'info');
      } catch (initError) {
        console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 초기화 실패:`, initError);
        showToast('디바이스 목록 초기화 중 오류가 발생했습니다.', 'warning');
      }
    } else {
      console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로가 정상적으로 존재합니다.`);
      
      // 데이터 유효성 검증
      const devices = snapshot.val();
      if (!Array.isArray(devices) && typeof devices !== 'object') {
        console.warn(`[DEVICE_PROTECTION] ${userId}의 디바이스 데이터가 유효하지 않습니다. 빈 배열로 교체합니다.`);
        
        try {
          await set(devicesRef, []);
          console.log(`[DEVICE_PROTECTION] ${userId}의 잘못된 디바이스 데이터를 빈 배열로 교체했습니다.`);
          showToast('디바이스 목록이 안전하게 복원되었습니다.', 'info');
        } catch (fixError) {
          console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 데이터 교체 실패:`, fixError);
        }
      }
    }
    
    // 추가 보호 - 프로필과 FCM 토큰 경로도 확인
    await protectAdditionalUserPaths(userId);
    
  } catch (error) {
    console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 보호 검사 실패:`, error);
    
    // 권한 오류인 경우 특별 처리
    if (error.code === 'PERMISSION_DENIED') {
      console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 접근 권한 거부됨`);
      showToast('디바이스 경로 접근 권한이 거부되었습니다.', 'warning');
    } else {
      console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 보호 중 예상치 못한 오류:`, error);
    }
  }
};

/**
 * 추가 사용자 경로 보호 (프로필, FCM 토큰 등)
 * @param {string} userId - 사용자 UID
 */
const protectAdditionalUserPaths = async (userId) => {
  try {
    // 프로필 경로 확인 및 초기화
    const profileRef = ref(database, `users/${userId}/profile`);
    const profileSnapshot = await get(profileRef);
    
    if (!profileSnapshot.exists()) {
      console.log(`[DEVICE_PROTECTION] ${userId}의 프로필 경로 초기화`);
      await set(profileRef, {
        bio: '',
        location: '',
        website: '',
        createdAt: new Date().toISOString()
      });
    }
    
    // FCM 토큰 경로 확인 및 초기화
    const fcmTokensRef = ref(database, `users/${userId}/fcmTokens`);
    const fcmSnapshot = await get(fcmTokensRef);
    
    if (!fcmSnapshot.exists()) {
      console.log(`[DEVICE_PROTECTION] ${userId}의 FCM 토큰 경로 초기화`);
      await set(fcmTokensRef, {});
    }
    
    console.log(`[DEVICE_PROTECTION] ${userId}의 모든 중요 경로 보호 완료`);
    
  } catch (error) {
    console.error(`[DEVICE_PROTECTION] ${userId}의 추가 경로 보호 실패:`, error);
  }
};

/**
 * 실시간 디바이스 경로 감시 설정
 * @param {string} userId - 사용자 UID
 */
const setupDevicePathWatcher = (userId) => {
  if (!userId) {
    console.warn('[DEVICE_PROTECTION] 디바이스 경로 감시: 사용자 ID가 없습니다.');
    return;
  }

  console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 실시간 감시 시작`);
  
  const devicesRef = ref(database, `users/${userId}/devices`);
  
  // 기존 리스너가 있다면 제거
  if (window.devicePathWatcher) {
    window.devicePathWatcher();
    delete window.devicePathWatcher;
  }
  
  // 실시간 리스너 설정
  window.devicePathWatcher = onValue(devicesRef, (snapshot) => {
    if (!snapshot.exists()) {
      // 디바이스 경로가 삭제된 경우 즉시 복원
      console.warn(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로가 삭제되었습니다! 즉시 복원합니다.`);
      
      set(devicesRef, [])
        .then(() => {
          console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로가 성공적으로 복원되었습니다.`);
          showToast('디바이스 목록이 자동으로 복원되었습니다.', 'warning');
        })
        .catch((error) => {
          console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 복원 실패:`, error);
          showToast('디바이스 목록 복원 중 오류가 발생했습니다.', 'error');
        });
    }
  }, (error) => {
    console.error(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 감시 오류:`, error);
  });
  
  console.log(`[DEVICE_PROTECTION] ${userId}의 디바이스 경로 감시가 활성화되었습니다.`);
};

/**
 * 디바이스 경로 감시 해제
 */
const stopDevicePathWatcher = () => {
  if (window.devicePathWatcher) {
    console.log('[DEVICE_PROTECTION] 디바이스 경로 감시를 중지합니다.');
    window.devicePathWatcher();
    delete window.devicePathWatcher;
  }
}; 